import React, { useState } from "react";
import { AuthIncLogo, AuthImg, PostingImg, TrackingHologramImg, TamperProofImg } from "../assets/images";
import { getHologramInfo } from "../clients/AuthIncClient";
import { Hologram } from "../types/Hologram";
import { useHistory } from "react-router-dom";
import RouteConstants from "../constants/RouteConstants";
import { Box, BoxProps, Button, Grid, GridProps, styled, TextField, Typography } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Headline from "../components/headline/Headline";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const Auth = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const FeaturetteBox = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.primary.main}`,
}));

interface FeaturetteProps extends BoxProps {
  gray?: boolean;
}

const Featurette = styled(Grid)<FeaturetteProps>((props) => ({
  display: "flex",
  background: props.gray ? props.theme.palette.gray.main : "none",
  padding: `${props.theme.spacing(3)} ${props.theme.spacing(5)}`,
  borderBottom: `1px solid ${props.theme.palette.primary.main}`,
}));

interface FeaturetteImgBoxProps extends GridProps {
  imgRight?: boolean;
}

const FeaturetteImgBox = styled(Grid)<FeaturetteImgBoxProps>((props) => ({
  textAlign: "center",
  margin: `${props.theme.spacing(1)} 0`,
  order: props.imgRight ? 2 : 0,
  [props.theme.breakpoints.down("md")]: {
    order: 0,
  },
}));

const FeaturetteImg = styled("img")({
  borderRadius: "50%",
  width: 200,
});

const FeaturetteText = styled(Grid)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const FeaturetteTextHeader = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: "bold",
  marginBottom: theme.spacing(1),
}));

const FeaturetteTextBody = styled(Typography)({
  textAlign: "justify",
});

const Home: React.FC = () => {
  const history = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [loading, setLoading] = useState(false);
  const [hologramId, setHologramId] = useState("");
  const [lookupError, setLookupError] = useState(false);

  const getHologram = async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }

    try {
      setLoading(true);

      const token = await executeRecaptcha("authenticate");

      const hologram: Hologram = await getHologramInfo({ recaptchaToken: token, hologram: hologramId });
      history.push(RouteConstants.DETAILS, { hologram });
    } catch (e) {
      console.error(e);
      setLookupError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <HeroContent />
      <Auth mb={4}>
        <Typography variant="body1" mb={2}>
          Enter your hologram information to find your item in our authentication database
        </Typography>
        {lookupError && (
          <Typography variant="subtitle1" color="red">
            Invalid Hologram ID
          </Typography>
        )}
        <TextField
          id="hologram-id"
          label="Hologram ID"
          InputLabelProps={{ shrink: true }}
          value={hologramId}
          onChange={(event) => setHologramId(event.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              getHologram();
            }
          }}
          variant="outlined"
          placeholder="example: cv574890"
          error={lookupError}
          sx={{ "& .MuiFilledInput-input": { py: 2 }, mb: 2 }}
        />
        <Button onClick={getHologram} variant="contained" disabled={loading} sx={{ width: 200 }}>
          {loading ? "Searching..." : "Authenticate"}
        </Button>
      </Auth>
      <FeaturetteBox>
        <Featurette container gray>
          <FeaturetteImgBox item lg={3} md={4} xs={12}>
            <FeaturetteImg src={AuthIncLogo} alt="Authenticators Inc. logo" />
          </FeaturetteImgBox>
          <FeaturetteText item lg={9} md={8} xs={12}>
            <FeaturetteTextHeader variant="h5">Background</FeaturetteTextHeader>
            <FeaturetteTextBody variant="body1">
              Since its launch in 2006, Authenticators, Inc. (A.I) has become the industry standard for autographed and
              game-used memorabilia authentication. Designed to distinguish officially authenticated memorabilia from
              other items on the market, the program offers an objective third-party authentication system that
              guarantees genuine memorabilia for collectors all over the world.
            </FeaturetteTextBody>
          </FeaturetteText>
        </Featurette>
        <Featurette container>
          <FeaturetteImgBox item lg={3} md={4} xs={12} imgRight>
            <FeaturetteImg src={AuthImg} alt="Authenticating item" />
          </FeaturetteImgBox>
          <FeaturetteText item lg={9} md={8} xs={12}>
            <FeaturetteTextHeader variant="h5">Authentication</FeaturetteTextHeader>
            <FeaturetteTextBody variant="body1">
              Off–duty law enforcement officers working as independent contractors are assigned by Authenticators, Inc.
              to authenticate autographs and event-used memorabilia. These authenticators must directly witness all
              items being signed and/or used in an event in order to authenticate them.
            </FeaturetteTextBody>
          </FeaturetteText>
        </Featurette>
        <Featurette container gray>
          <FeaturetteImgBox lg={3} item md={4} xs={12}>
            <FeaturetteImg src={PostingImg} alt="Putting hologram on the bottom of a base" />
          </FeaturetteImgBox>
          <FeaturetteText lg={9} item md={8} xs={12}>
            <FeaturetteTextHeader variant="h5">Tamper-Proof Holograms</FeaturetteTextHeader>
            <FeaturetteTextBody variant="body1">
              Once witnessed, items receive a tamper-proof hologram created by OpSec, U.S. with a unique alpha-numeric
              combination to easily identify its authenticity. Collectors can easily distinguish officially
              authenticated memorabilia from other items by locating this official hologram.
            </FeaturetteTextBody>
          </FeaturetteText>
        </Featurette>
        <Featurette container>
          <FeaturetteImgBox item lg={3} md={4} xs={12} imgRight>
            <FeaturetteImg src={TrackingHologramImg} alt="Hologram sticker" />
          </FeaturetteImgBox>
          <FeaturetteText lg={9} item md={8} xs={12}>
            <FeaturetteTextHeader variant="h5">Tracking the Items</FeaturetteTextHeader>
            <FeaturetteTextBody variant="body1">
              Once collectors receive their authenticated piece of memorabilia, they can trace the unique serial number
              on the Authentication database located on authenticatorsinc.com. This convenient reference database
              enables collectors to easily identify all of the details surrounding their new item. Since its inception
              in 2006, Authenticators, Inc. has authenticated over 6 million items, allowing fans to ensure their
              memorabilia is 100% genuine.
            </FeaturetteTextBody>
          </FeaturetteText>
        </Featurette>
        <Featurette container gray>
          <FeaturetteImgBox lg={3} item md={4} xs={12}>
            <FeaturetteImg src={TamperProofImg} alt="Tamper-proof hologram" />
          </FeaturetteImgBox>
          <FeaturetteText lg={9} item md={8} xs={12}>
            <FeaturetteTextHeader variant="h5">Security</FeaturetteTextHeader>
            <FeaturetteTextBody variant="body1">
              In order to ensure the security of authenticated items, holograms are designed to show traces of tampering
              if a hologram is removed. This image shows holograms that have been removed from items and are no longer
              considered valid.
            </FeaturetteTextBody>
          </FeaturetteText>
        </Featurette>
      </FeaturetteBox>
    </Box>
  );
};

const STREAMABLE_EMBED_SRC =
  "https://streamable.com/m/mlb-authentication-program?partnerId=web_video-playback-page_video-share";

const HeroContent = () => {
  const [heroVideoOpen, setHeroVideoOpen] = useState(false);

  const closeHeroVideo = () => {
    setHeroVideoOpen(false);
  };

  return heroVideoOpen ? (
    <Headline marginBottom={4}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box sx={{ display: "flex", justifyContent: "flex-end", width: 560, maxWidth: "100%", mb: 0.5 }}>
          <Button onClick={closeHeroVideo} color="primary" size="small" variant="text" sx={{ textTransform: "none" }}>
            Close
          </Button>
        </Box>
        <Box
          component="iframe"
          src={STREAMABLE_EMBED_SRC}
          title="MLB Authentication Program"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          sx={{
            width: 560,
            maxWidth: "100%",
            height: 315,
            border: 0,
            bgcolor: "black",
            display: "block",
            borderRadius: 0.5,
          }}
        />
      </Box>
    </Headline>
  ) : (
    <Headline marginBottom={4}>
      <Typography variant="h3" color="white" mb={2}>
        Authenticators, Inc.
      </Typography>
      <Typography variant="body1" color="primary" sx={{ fontWeight: "lighter", mb: 2 }}>
        See how Authenticators, Inc. verifies genuine memorabilia
      </Typography>
      <Button
        variant="contained"
        endIcon={<ArrowRightIcon />}
        onClick={() => setHeroVideoOpen(true)}
        sx={{
          "& .MuiButton-endIcon > :nth-of-type(1)": {
            fontSize: 32,
          },
          width: 170,
        }}
      >
        Watch Now
      </Button>
    </Headline>
  );
};
export default Home;
